import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import getTenantIdFromURL from './utils/getTenantIdFromURL';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import { DataGridPremium,
         useGridApiRef,
         GridActionsCellItem,
         GridToolbar,
} from '@mui/x-data-grid-premium';
import requestStatusService from "./api/tenantApi"


export default function RequestStatus() {
  const location = useLocation();
  const URLTenant = getTenantIdFromURL();
  const isAdmin = useSelector(state => state.userInfo.isAdmin);
  const isTenantDashboard = location.pathname.includes('/tenant-dashboard/');
  const apiRef = useGridApiRef();
  const [editingRowId, setEditingRowId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentTenantId = useSelector(state => state.userInfo.tenantId);
  const [tenantId, setTenantId] = useState();
  

  useEffect(() => {
    const selectTenantId = () => {
      if (!currentTenantId && !URLTenant) {
        console.log("Waiting for currentTenantId and URLTenant to be available...");
        return;
      }
      if (isAdmin && isTenantDashboard) {
        setTenantId(URLTenant);
      } else if (isAdmin && !isTenantDashboard) {
        setTenantId(null);
      } else {
        setTenantId(currentTenantId);
      }
    };
    selectTenantId();
  }, [isAdmin, isTenantDashboard, currentTenantId, URLTenant]);
  

  useEffect(() => {
    if (tenantId) {
      fetchRequestsForTenant();
    } else if (tenantId == null && isAdmin && !isTenantDashboard) {
      fetchRequestsForAdmin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, isAdmin, isTenantDashboard]);
  

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');       
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();                          
      const localTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); 
    return `${day}-${month}-${year}  ${localTime}`; 
  };

  const fetchRequestsForTenant = async () => {
    try {
      
      setLoading(true);
      let allStatuses = [];
      let nextToken = null;

    
        const response = await requestStatusService.getRequestStatusesByTenantId(tenantId);
        console.log("🚀 ~ fetchRequestsForTenant ~ response:", response)
        

        const data = response?.data;
        console.log("🚀 ~ fetchRequestsForTenant ~ data:", data)
        

      const filtered = tenantId
        ? data.filter(item => item.tenantId === tenantId)
        : allStatuses;

      console.log("Filtered rows:", filtered);

      const formattedRows = filtered.map(item => ({
        id: item.id,
        name: item.companyName,
        tenantName: item.tenantName,
        status: item.requestStatus,
        date: new Date(item.requestDate),               
        RequestChangeDate: new Date(item.statusChangeDate),
        comment: item.comments,
        email: item.email,
        phone: item.phoneNo,
        requestType: item.requestType,
        tenantId: item.tenantId,
      }));

      setRows(formattedRows);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestsForAdmin = async () => {
    try {
      setLoading(true);
      let allStatuses = [];
      let nextToken = null;
  
     
        const response = await requestStatusService.getAllRequestStatuses();

        console.log("Response:", response);
  
        const data = response?.data;
       
  
      console.log("All rows:", allStatuses);
  
      const formattedRows = data.map(item => ({
        id: item.id,
        name: item.companyName,
        tenantName: item.tenantName,
        status: item.requestStatus,
        date: new Date(item.requestDate),              
        RequestChangeDate: new Date(item.statusChangeDate),
        comment: item.comments,
        email: item.email,
        phone: item.phoneNo,
        requestType: item.requestType,
        tenantId: item.tenantId,
      }));
  
      setRows(formattedRows);
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };


  const handleEditClick = (id) => {
    if (!isAdmin) return;
    setEditingRowId(id);
    apiRef.current.startRowEditMode({ id });
  };

  const handleSaveClick = async (id) => {
    if (!isAdmin) return;

    try {

      const row = apiRef.current.getRowWithUpdatedValues(id);
      console.log('Updated row:', row);

      const response = await requestStatusService.updateRequestStatus(row.id,{
            id: row.id,
            tenantId: row.tenantId,
            requestStatus: row.status,
            statusChangeDate: new Date().toISOString(),
            comments: row.comment
          })

      console.log("Response:", response.data);
      apiRef.current.stopRowEditMode({ id });
      setEditingRowId(null);
      toast.success('Request updated successfully');
      if (tenantId) {
        fetchRequestsForTenant();
      } 
      if(tenantId===null && isAdmin && !isTenantDashboard){
        fetchRequestsForAdmin();
      }
    } catch (error) {
      console.error("Error in handleSaveClick:", error);
      console.error("Error stack:", error.stack);
      toast.error('Failed to update request');
    }
  };


  const handleCancelClick = (id) => {
    if (!isAdmin) return;
    apiRef.current.stopRowEditMode({ id, ignoreModifications: true });
    setEditingRowId(null);
  };

  const processRowUpdate = (newRow) => {
    if (!isAdmin) return newRow;
    const updatedRows = rows.map((row) => (row.id === newRow.id ? newRow : row));
    setRows(updatedRows);
    return newRow;
  };

  const adminColumns = [
    { field: 'name', headerName: 'Company Name', flex: 1, editable: false },
    { field: 'tenantName', headerName: 'Tenant Name', flex: 1, editable: false },
    {
      field: 'status',
      headerName: 'Request Status',
      flex: 1,
      type: 'singleSelect',
      valueOptions: ['Sent', 'Processing', 'Approved', 'Rejected'],
      editable: (params) => params.id === editingRowId,
    },
    { field: 'date', headerName: 'Receive Date', flex: 1 },
    { field: 'RequestChangeDate', headerName: 'Req. Change Date', flex: 1 },
    { field: 'comment', headerName: 'Comment', flex: 2, editable: (params) => params.id === editingRowId },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'phone', headerName: 'Phone No.', flex: 1 },
    { field: 'requestType', headerName: 'Request Type', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      flex: 0.5,
      getActions: (params) => {
        const isEditing = params.id === editingRowId;
        if (isEditing) {
          return [
            <GridActionsCellItem
              key="save"
              icon={<SaveIcon sx={{ color: 'black' }} />}
              label="Save"
              onClick={() => handleSaveClick(params.id)}
            />,
            <GridActionsCellItem
              key="cancel"
              icon={<CancelIcon sx={{ color: 'black' }} />}
              label="Cancel"
              onClick={() => handleCancelClick(params.id)}
            />,
          ];
        }
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon sx={{ color: 'black' }} />}
            label="Edit"
            onClick={() => handleEditClick(params.id)}
          />
        ];
      },
    },
  ];

  const userColumns = [
    { field: 'name', headerName: 'Company Name', flex: 1 },
    { field: 'requestType', headerName: 'Request Type', flex: 1 },
    { field: 'status', headerName: 'Request Status', flex: 1 },
    { field: 'date', headerName: 'Sending Date', flex: 1 },
    { field: 'RequestChangeDate', headerName: 'Request Change Date', flex: 1 },
    { field: 'comment', headerName: 'Comment', flex: 2 },
  ];

  const getColumns = () => {
    if (isAdmin && !isTenantDashboard) {
      return adminColumns;
    } else if (isAdmin && isTenantDashboard) {
      return adminColumns;
    } else {
      return userColumns;
    }
  };

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <DataGridPremium
        apiRef={apiRef}
        rows={rows}
        columns={getColumns()}
        rowHeight={35}
        checkboxSelection={isAdmin && !isTenantDashboard}
        disableRowSelectionOnClick
        editMode="none"
        processRowUpdate={processRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
        loading={loading}
        initialState={{
          sorting: {
            sortModel: [{ field: 'date', sort: 'desc' }],
          },
          }}
        slots={{
          toolbar: GridToolbar,
        }}
        slotProps={{
          toolbar: {
            csvOptions: {
              fields: ['date', 'comment', 'tenantName', 'name', 'status'],
            },
          },
        }}
      />
    </Box>
  );
}

